import type { ApprovalNode, WorkflowDefinitionStep } from 'types/SPM';

export type WorkflowRunStepStatus = 'past' | 'active' | 'future';

/** One row in the run detail list: an executed step carries its `approvalNode`, a future or
 * container step carries only a `label`. Repeat-By body steps nest under `children`. */
export type WorkflowRunStep = {
  definitionStepId: string;
  type: string;
  status: WorkflowRunStepStatus;
  approvalNode?: ApprovalNode;
  label: string;
  children?: WorkflowRunStep[];
};

type Translate = (key: string, params?: Record<string, string>) => string;

const RUN_DETAIL_KEY = 'SPM.Automation.run_detail';
const STEP_LABEL_KEY = `${RUN_DETAIL_KEY}.step_label`;

const STEP_TYPE = {
  ADD_APPROVAL: 'add_approval',
  REPEAT_BY: 'repeat_by',
  APPLY_CHANGES: 'spm_apply_proposed_change_group',
  RETURN_TO_STEP: 'return_to_step',
  CONDITION: 'condition',
} as const;

const STEP_LABEL_KEYS: Record<string, string> = {
  [STEP_TYPE.ADD_APPROVAL]: `${STEP_LABEL_KEY}.add_approval`,
  [STEP_TYPE.APPLY_CHANGES]: `${STEP_LABEL_KEY}.apply_changes`,
  [STEP_TYPE.RETURN_TO_STEP]: `${STEP_LABEL_KEY}.return_to_step`,
  [STEP_TYPE.CONDITION]: `${STEP_LABEL_KEY}.condition`,
};

type StepMetadata = {
  decision_cases?: { approved?: WorkflowDefinitionStep[]; declined?: WorkflowDefinitionStep[] };
  body?: WorkflowDefinitionStep[];
  switch?: Array<{ then?: WorkflowDefinitionStep[] }>;
  selector?: { config?: { attribute_label?: string; value_label?: string } };
};

const metadataOf = (step: WorkflowDefinitionStep): StepMetadata => (step.metadata ?? {}) as StepMetadata;

const decisionBranches = (
  step: WorkflowDefinitionStep,
): { approved: WorkflowDefinitionStep[]; declined: WorkflowDefinitionStep[] } => {
  const cases = metadataOf(step).decision_cases;
  return { approved: cases?.approved ?? [], declined: cases?.declined ?? [] };
};

const hasDecisionBranches = (step: WorkflowDefinitionStep): boolean => {
  const { approved, declined } = decisionBranches(step);
  return approved.length > 0 || declined.length > 0;
};

const repeatByBody = (step: WorkflowDefinitionStep): WorkflowDefinitionStep[] => metadataOf(step).body ?? [];

const conditionBranches = (step: WorkflowDefinitionStep): WorkflowDefinitionStep[] =>
  (metadataOf(step).switch ?? []).flatMap((switchCase) => switchCase.then ?? []);

const interleave = (
  approved: WorkflowDefinitionStep[],
  declined: WorkflowDefinitionStep[],
): WorkflowDefinitionStep[] => {
  const result: WorkflowDefinitionStep[] = [];
  const rounds = Math.max(approved.length, declined.length);
  for (let i = 0; i < rounds; i += 1) {
    if (i < approved.length) result.push(approved[i]);
    if (i < declined.length) result.push(declined[i]);
  }
  return result;
};

const takenBranch = (step: WorkflowDefinitionStep, node: ApprovalNode): WorkflowDefinitionStep[] => {
  const { approved, declined } = decisionBranches(step);
  return node.is_approved === false ? declined : approved;
};

/** workflow-service builds an approval's wait step by appending this to the definition step's id,
 * in `ciq_definition_translator.py`, and the run reports the wait step. */
const VERDICT_SUFFIX = '_verdict';

const findStepById = (steps: WorkflowDefinitionStep[], stepId: string): WorkflowDefinitionStep | null => {
  for (const step of steps) {
    if (step.id === stepId) return step;
    const { approved, declined } = decisionBranches(step);
    const found = findStepById([...approved, ...declined, ...repeatByBody(step), ...conditionBranches(step)], stepId);
    if (found) return found;
  }
  return null;
};

/** Finds the definition step for an approval group by stripping `_verdict` from the run node's step id. */
export const findApprovalDefinitionStep = (
  template: WorkflowDefinitionStep[],
  approvalNodes: ApprovalNode[],
  approvalGroupId: string,
): WorkflowDefinitionStep | null => {
  const node = approvalNodes.find((candidate) =>
    candidate.approvers.some((approver) => approver.approval_group_id === approvalGroupId),
  );
  if (!node?.step_id.endsWith(VERDICT_SUFFIX)) return null;
  return findStepById(template, node.step_id.slice(0, -VERDICT_SUFFIX.length));
};

/** Only checks direct children of the reject path — nested loopbacks depend on runtime branch decisions. */
export const rejectPathReturnsToStep = (step: WorkflowDefinitionStep | null): boolean =>
  step !== null && decisionBranches(step).declined.some((child) => child.type === STEP_TYPE.RETURN_TO_STEP);

/**
 * Merge a workflow definition template with the executed approval nodes into one flat, ordered step
 * list. Executed nodes are consumed in order; every unmatched definition step renders as future.
 *
 * Branch selection is verdict-guided: at each approval the consumed node's own `is_approved` picks
 * the branch to follow. A Repeat-By fans out per branch and folds by step, so more than one node can
 * be active at once — the walk keeps consuming nodes down the taken branch rather than stopping at
 * the first active one.
 */
export const buildWorkflowRunSteps = (
  template: WorkflowDefinitionStep[],
  approvalNodes: ApprovalNode[],
  t: Translate,
): WorkflowRunStep[] => {
  const repeatByLabel = (step: WorkflowDefinitionStep): string => {
    const config = metadataOf(step).selector?.config;
    const attribute = config?.attribute_label;
    if (typeof attribute === 'string' && attribute) {
      const value = config?.value_label;
      return typeof value === 'string' && value
        ? t(`${RUN_DETAIL_KEY}.repeat_by_label_with_value`, { attribute, value })
        : t(`${RUN_DETAIL_KEY}.repeat_by_label`, { attribute });
    }
    return t(`${STEP_LABEL_KEY}.repeat_by_fallback`);
  };

  const stepLabel = (step: WorkflowDefinitionStep): string =>
    step.type === STEP_TYPE.REPEAT_BY
      ? repeatByLabel(step)
      : t(STEP_LABEL_KEYS[step.type] ?? `${STEP_LABEL_KEY}.fallback`);

  const plainStep = (step: WorkflowDefinitionStep, status: WorkflowRunStepStatus): WorkflowRunStep => ({
    definitionStepId: step.id,
    type: step.type,
    status,
    label: stepLabel(step),
  });

  const walkFuture = (seed: WorkflowDefinitionStep[], target: WorkflowRunStep[]): void => {
    let level = seed;
    while (level.length > 0) {
      const next: WorkflowDefinitionStep[] = [];
      for (const step of level) {
        if (step.type === STEP_TYPE.REPEAT_BY) {
          const container = { ...plainStep(step, 'future'), children: [] as WorkflowRunStep[] };
          target.push(container);
          walkFuture(repeatByBody(step), container.children);
        } else {
          target.push(plainStep(step, 'future'));
          if (step.type === STEP_TYPE.ADD_APPROVAL && hasDecisionBranches(step)) {
            const { approved, declined } = decisionBranches(step);
            next.push(...interleave(approved, declined));
          } else if (step.type === STEP_TYPE.CONDITION) {
            next.push(...conditionBranches(step));
          }
        }
      }
      level = next;
    }
  };

  const consume = (nodeQueue: ApprovalNode[], sequence: WorkflowDefinitionStep[], target: WorkflowRunStep[]): void => {
    for (let index = 0; index < sequence.length; index += 1) {
      if (nodeQueue.length === 0) {
        walkFuture(sequence.slice(index), target);
        return;
      }
      const step = sequence[index];

      if (step.type === STEP_TYPE.ADD_APPROVAL) {
        const node = nodeQueue[0];
        nodeQueue.shift();
        target.push({
          definitionStepId: step.id,
          type: step.type,
          status: node.status,
          approvalNode: node,
          label: t(`${STEP_LABEL_KEY}.add_approval`),
        });
        if (nodeQueue.length === 0) {
          const { approved, declined } = decisionBranches(step);
          walkFuture(node.is_approved === null ? interleave(approved, declined) : takenBranch(step, node), target);
        } else {
          consume(nodeQueue, takenBranch(step, node), target);
        }
      } else if (step.type === STEP_TYPE.REPEAT_BY) {
        const container = { ...plainStep(step, 'past'), children: [] as WorkflowRunStep[] };
        target.push(container);
        consume(nodeQueue, repeatByBody(step), container.children);
      } else if (step.type === STEP_TYPE.CONDITION) {
        target.push(plainStep(step, 'past'));
        consume(nodeQueue, conditionBranches(step), target);
      } else if (step.type === STEP_TYPE.RETURN_TO_STEP) {
        target.push(plainStep(step, 'past'));
        return;
      } else {
        target.push(plainStep(step, 'past'));
      }
    }
  };

  const steps: WorkflowRunStep[] = [];
  const nodeQueue = [...approvalNodes];

  if (nodeQueue.length === 0) {
    walkFuture(template, steps);
    return steps;
  }

  consume(nodeQueue, template, steps);

  for (const node of nodeQueue) {
    steps.push({
      definitionStepId: node.step_id,
      type: STEP_TYPE.ADD_APPROVAL,
      status: node.status,
      approvalNode: node,
      label: t(`${STEP_LABEL_KEY}.unknown`),
    });
  }

  return steps;
};
